import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { processSteps, manufacturingImages } from '../data/mockData';

const Process = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="text-sm font-semibold text-brand-teal uppercase tracking-wider">
                Our Methodology
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-light leading-tight mb-6">
              From Idea to <span className="font-medium text-brand-teal">Market Launch</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              A streamlined, transparent development process designed to accelerate your product journey 
              while maintaining scientific rigor and regulatory compliance at every stage.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border transform -translate-x-1/2"></div>

            {processSteps.map((step, index) => (
              <div key={step.step} className="relative mb-16 lg:mb-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                  {/* Left Content (odd steps) */}
                  {index % 2 === 0 && (
                    <>
                      <div className="lg:text-right lg:pr-12">
                        <div className="inline-block mb-4">
                          <span className="text-sm font-semibold text-brand-teal">
                            Step {String(step.step).padStart(2, '0')}
                          </span>
                        </div>
                        <h3 className="text-2xl lg:text-3xl font-medium mb-4">
                          {step.title}
                        </h3>
                        <p className="text-base text-muted-foreground leading-relaxed mb-4">
                          {step.description}
                        </p>
                        <div className="flex items-center gap-2 lg:justify-end">
                          <Clock size={16} className="text-brand-teal" />
                          <span className="text-sm text-muted-foreground">{step.duration}</span>
                        </div>
                      </div>
                      <div className="hidden lg:block"></div>
                    </>
                  )}

                  {/* Right Content (even steps) */}
                  {index % 2 === 1 && (
                    <>
                      <div className="hidden lg:block"></div>
                      <div className="lg:pl-12">
                        <div className="inline-block mb-4">
                          <span className="text-sm font-semibold text-brand-teal">
                            Step {String(step.step).padStart(2, '0')}
                          </span>
                        </div>
                        <h3 className="text-2xl lg:text-3xl font-medium mb-4">
                          {step.title}
                        </h3>
                        <p className="text-base text-muted-foreground leading-relaxed mb-4">
                          {step.description}
                        </p>
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="text-brand-teal" />
                          <span className="text-sm text-muted-foreground">{step.duration}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Timeline Dot */}
                  <div className="hidden lg:block absolute left-1/2 top-8 transform -translate-x-1/2">
                    <div className="w-6 h-6 rounded-full bg-brand-teal border-4 border-background"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total Timeline */}
          <div className="mt-16 p-8 bg-secondary text-center">
            <p className="text-sm font-semibold text-brand-teal uppercase tracking-wider mb-2">
              Total Development Timeline
            </p>
            <p className="text-3xl font-light">
              12-20 Weeks <span className="text-lg text-muted-foreground">(Typical)</span>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Expedited timelines available for urgent projects
            </p>
          </div>
        </div>
      </section>

      {/* Manufacturing Support */}
      <section className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-block mb-6">
                <span className="text-sm font-semibold text-brand-teal uppercase tracking-wider">
                  Beyond Formulation
                </span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Seamless <span className="font-medium text-brand-teal">Scale-Up Support</span>
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-6">
                Our in-house manufacturing capabilities exist to support rapid prototyping and pilot 
                batch production, ensuring smooth transition from lab to commercial scale.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  "Small-batch pilot production (100-1000 units)",
                  "Process optimization and documentation",
                  "Scale-up troubleshooting support",
                  "Manufacturing partner recommendations"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0"></div>
                    <span className="text-base">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center text-brand-teal font-semibold hover:gap-3 transition-all"
              >
                Discuss Scale-Up Plans
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={manufacturingImages.cleanRoom}
                alt="Manufacturing facility"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto bg-foreground text-white p-12 lg:p-16">
            <div className="text-center">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Ready to Begin Your Product Journey?
              </h2>
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-2xl mx-auto">
                Schedule a consultation to discuss your timeline, budget, and product requirements. 
                We'll create a customized development roadmap for your brand.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-foreground bg-white hover:bg-white/90"
              >
                Request Development Roadmap
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Process;
